import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { validateEmail, validatePassword, validateReferralCode, validateEmailDomain, sanitizeInput } from '../../../utils/validation';
import { supabase } from '../../../lib/supabase';

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAlreadySignedIn, setIsAlreadySignedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    country: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    referralCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Check if user is already signed in
  useEffect(() => {
    let isMounted = true;
    
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && isMounted) {
          setIsAlreadySignedIn(true);
          setUserEmail(session.user.email || '');
          // Use window.location for reliable redirect
          setTimeout(() => {
            if (isMounted) {
              window.location.href = '/';
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Session check error:', error);
        // Don't block the page on error
      }
    };
    
    checkUser();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Handle referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref') || searchParams.get('referral');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode }));
    }
  }, [searchParams]);

  // If user is already signed in, show welcome back message
  if (isAlreadySignedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* PepCoin Logo */}
          <div className="text-center mb-8">
            <a href="/" className="inline-flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-green-400 via-green-500 to-green-600 p-1 shadow-lg">
                <div className="w-full h-full bg-white rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-black text-2xl" style={{ fontFamily: '"Pacifico", serif' }}>P</span>
                </div>
              </div>
              <div className="text-left">
                <div className="text-white font-bold text-3xl" style={{ fontFamily: '"Pacifico", serif' }}>
                  PepCoin
                </div>
                <div className="text-green-400 text-sm font-medium">
                  The Future of Crypto
                </div>
              </div>
            </a>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-lg border border-purple-500/30 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                <i className="ri-user-smile-line text-4xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Welcome Back!</h2>
              <p className="text-gray-400">You're already signed in</p>
              {userEmail && (
                <p className="text-purple-400 mt-2 text-sm">{userEmail}</p>
              )}
            </div>

            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-green-400 text-sm">Redirecting you to home...</p>
              </div>
            </div>

            <div className="space-y-4">
              <a
                href="/"
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/25 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-home-line"></i>
                Go to Home
              </a>

              <a
                href="/profile"
                className="w-full py-3 px-4 bg-gray-700/50 text-white rounded-xl font-semibold hover:bg-gray-700 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-user-line"></i>
                Go to Profile
              </a>
            </div>

            {/* Quick Links */}
            <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                <i className="ri-links-line"></i>
                Quick Access
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <a href="/wallet" className="text-blue-300 text-sm hover:text-blue-200 cursor-pointer transition-colors flex items-center gap-1">
                  <i className="ri-wallet-line"></i>
                  Wallet
                </a>
                <a href="/trading" className="text-blue-300 text-sm hover:text-blue-200 cursor-pointer transition-colors flex items-center gap-1">
                  <i className="ri-line-chart-line"></i>
                  Trading
                </a>
                <a href="/staking" className="text-blue-300 text-sm hover:text-blue-200 cursor-pointer transition-colors flex items-center gap-1">
                  <i className="ri-coin-line"></i>
                  Staking
                </a>
                <a href="/referral" className="text-blue-300 text-sm hover:text-blue-200 cursor-pointer transition-colors flex items-center gap-1">
                  <i className="ri-gift-line"></i>
                  Referral
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    // Country validation
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    // Email validation - simplified and more flexible
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms validation
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    // Referral code validation - now optional
    // No validation needed - referral code is optional

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate form
    if (!validateForm()) {
      setError('Please fix the errors below and try again');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/simple-registration-v3`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'signup',
          email: sanitizeInput(formData.email.trim().toLowerCase()),
          password: formData.password,
          firstName: sanitizeInput(formData.firstName.trim()),
          lastName: sanitizeInput(formData.lastName.trim()),
          country: sanitizeInput(formData.country.trim()),
          referralCode: sanitizeInput(formData.referralCode.trim().toUpperCase())
        })
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(result.message || 'Account created successfully! Redirecting to sign in...');
        
        // Clear form
        setFormData({
          firstName: '',
          lastName: '',
          country: '',
          email: '',
          password: '',
          confirmPassword: '',
          agreeToTerms: false,
          referralCode: ''
        });
        setValidationErrors({});

        // Redirect to sign in after 3 seconds
        setTimeout(() => {
          navigate('/auth/signin?message=Account created successfully! Please sign in to access your account.&from=signup');
        }, 3000);
      } else {
        // Handle specific error cases with user-friendly messages
        if (result.error.includes('already registered') || result.error.includes('already exists')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else if (result.error.includes('Email is required')) {
          setError('Please enter your email address.');
        } else if (result.error.includes('Password must be')) {
          setError('Password must be at least 6 characters long.');
        } else if (result.error.includes('First name and last name')) {
          setError('Please enter your first and last name.');
        } else if (result.error.includes('Invalid email format')) {
          setError('Please enter a valid email address.');
        } else {
          setError(result.error || 'Registration failed. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      setError('Unable to connect to registration service. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Clear email suggestion when user changes email
    if (name === 'email' && emailSuggestion) {
      setEmailSuggestion('');
    }
  };

  const handleEmailSuggestionClick = () => {
    setFormData(prev => ({ ...prev, email: emailSuggestion }));
    setEmailSuggestion('');
    if (validationErrors.email) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  const handlePasswordToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword(!showPassword);
  };

  const handleConfirmPasswordToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleBackToHome = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Back Button */}
        <div className="flex justify-start mb-4">
          <button
            onClick={handleBackToHome}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer group"
          >
            <i className="ri-arrow-left-line group-hover:transform group-hover:-translate-x-1 transition-transform"></i>
            <span>Back to Home</span>
          </button>
        </div>

        {/* PepCoin Original Logo */}
        <div className="text-center mb-8">
          <button onClick={handleBackToHome} className="inline-flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-green-400 via-green-500 to-green-600 p-1 shadow-lg">
              <div className="w-full h-full bg-white rounded-full flex items-center justify-center">
                <span className="text-green-600 font-black text-2xl" style={{ fontFamily: '"Pacifico", serif' }}>P</span>
              </div>
            </div>
            <div className="text-left">
              <div className="text-white font-bold text-3xl" style={{ fontFamily: '"Pacifico", serif' }}>
                PepCoin
              </div>
              <div className="text-green-400 text-sm font-medium">
                The Future of Crypto
              </div>
            </div>
          </button>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-lg border border-purple-500/30 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Create Account</h2>
            <p className="text-gray-400">Join the future of cryptocurrency trading</p>
          </div>

          {/* Referral Code Display */}
          {formData.referralCode && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-2">
                <i className="ri-gift-line text-green-400"></i>
                <p className="text-green-400 text-sm">
                  Referral Code: <span className="font-mono font-bold">{formData.referralCode}</span>
                </p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-green-400"></i>
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-red-400 mt-0.5"></i>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-4 py-3 bg-gray-800/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm disabled:opacity-50 cursor-text ${
                    validationErrors.firstName ? 'border-red-500/50' : 'border-purple-500/30'
                  }`}
                  placeholder="First name"
                />
                {validationErrors.firstName && (
                  <p className="mt-1 text-xs text-red-400">{validationErrors.firstName}</p>
                )}
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-4 py-3 bg-gray-800/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm disabled:opacity-50 cursor-text ${
                    validationErrors.lastName ? 'border-red-500/50' : 'border-purple-500/30'
                  }`}
                  placeholder="Last name"
                />
                {validationErrors.lastName && (
                  <p className="mt-1 text-xs text-red-400">{validationErrors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-300 mb-2">
                Country
              </label>
              <div className="relative">
                <input
                  id="country"
                  name="country"
                  type="text"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-4 py-3 pl-10 bg-gray-800/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 disabled:opacity-50 cursor-text ${
                    validationErrors.country ? 'border-red-500/50' : 'border-purple-500/30'
                  }`}
                  placeholder="Enter your country"
                />
                <i className="ri-global-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
              {validationErrors.country && (
                <p className="mt-1 text-xs text-red-400">{validationErrors.country}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-4 py-3 pl-10 bg-gray-800/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 disabled:opacity-50 cursor-text ${
                    validationErrors.email ? 'border-red-500/50' : 'border-purple-500/30'
                  }`}
                  placeholder="Enter your email"
                />
                <i className="ri-mail-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
              {validationErrors.email && (
                <p className="mt-1 text-xs text-red-400">{validationErrors.email}</p>
              )}
              {emailSuggestion && (
                <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 text-xs">
                    Did you mean{' '}
                    <button
                      type="button"
                      onClick={handleEmailSuggestionClick}
                      className="underline hover:text-yellow-300 cursor-pointer"
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                </div>
              )}
              <p className="mt-1 text-xs text-gray-400">
                No email verification required - instant access!
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-4 py-3 pl-10 pr-10 bg-gray-800/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 disabled:opacity-50 cursor-text ${
                    validationErrors.password ? 'border-red-500/50' : 'border-purple-500/30'
                  }`}
                  placeholder="Create password (min 6 characters)"
                />
                <i className="ri-lock-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <button
                  type="button"
                  onClick={handlePasswordToggle}
                  disabled={loading}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-5 h-5 flex items-center justify-center"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
              {validationErrors.password && (
                <p className="mt-1 text-xs text-red-400">{validationErrors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-4 py-3 pl-10 pr-10 bg-gray-800/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 disabled:opacity-50 cursor-text ${
                    validationErrors.confirmPassword ? 'border-red-500/50' : 'border-purple-500/30'
                  }`}
                  placeholder="Confirm password"
                />
                <i className="ri-lock-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <button
                  type="button"
                  onClick={handleConfirmPasswordToggle}
                  disabled={loading}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-5 h-5 flex items-center justify-center"
                >
                  <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
              {validationErrors.confirmPassword && (
                <p className="mt-1 text-xs text-red-400">{validationErrors.confirmPassword}</p>
              )}
            </div>

            {/* Referral Code Input - Now Optional */}
            <div>
              <label htmlFor="referralCode" className="block text-sm font-medium text-gray-300 mb-2">
                Referral Code <span className="text-gray-500">(Optional)</span>
              </label>
              <div className="relative">
                <input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={formData.referralCode}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-4 py-3 pl-10 bg-gray-800/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 disabled:opacity-50 cursor-text ${
                    validationErrors.referralCode ? 'border-red-500/50' : 'border-purple-500/30'
                  }`}
                  placeholder="Enter referral code (optional)"
                />
                <i className="ri-gift-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
              {validationErrors.referralCode && (
                <p className="mt-1 text-xs text-red-400">{validationErrors.referralCode}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">
                Optional: Enter a referral code to earn bonus rewards for you and your referrer
              </p>
            </div>

            <div className="flex items-start">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                required
                checked={formData.agreeToTerms}
                onChange={handleChange}
                disabled={loading}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 rounded bg-gray-800 disabled:opacity-50 cursor-pointer mt-0.5"
              />
              <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-300 cursor-pointer">
                I agree to the{' '}
                <Link to="/terms" className="text-purple-400 hover:text-purple-300 cursor-pointer underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-purple-400 hover:text-purple-300 cursor-pointer underline">
                  Privacy Policy
                </Link>
              </label>
            </div>
            {validationErrors.agreeToTerms && (
              <p className="text-xs text-red-400">{validationErrors.agreeToTerms}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/25 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating Account...
                </>
              ) : (
                <>
                  <i className="ri-user-add-line"></i>
                  Create Account
                </>
              )}
            </button>
          </form>

          <p className="text-center text-gray-400 mt-6">
            Already have an account?{' '}
            <Link to="/auth/signin" className="text-purple-400 hover:text-purple-300 font-medium cursor-pointer transition-colors underline">
              Sign in
            </Link>
          </p>

          {/* Benefits */}
          <div className="mt-8 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
            <h3 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
              <i className="ri-gift-line"></i>
              Referral Program
            </h3>
            <ul className="text-green-300 text-sm space-y-1">
              <li>• Use a referral code to help your referrer earn $6</li>
              <li>• Referrer receives $6 after your first completed transaction</li>
              <li>• Immediate account activation - no email verification needed</li>
              <li>• Start trading right away</li>
              <li>• Secure authentication system</li>
              <li>• Access to exclusive member benefits</li>
            </ul>
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center">
          <div className="bg-gray-800/30 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-2">Simple & Secure</h4>
            <p className="text-gray-400 text-sm">
              Our enhanced registration system accepts all valid email formats and provides instant access. 
              No email verification delays - your account is ready immediately!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
